<template>

  <div class="file-card">

    <div class="file-header row d-flex align-items-center">

      <div class="col-auto px-0">
        <div class="icon-lg icon-shape d-flex justify-content-center">
          <i :class="`${icon}`" :style="{ color: color }"></i>
        </div>
      </div>
      
      <div class="col d-flex flex-column justify-content-center px-0">
        <p class="file-title text-sm mb-0">{{ title }}</p>
        <p class="file-meta text-xs mb-0">{{ dateTime }} • {{ size }}</p>
      </div>
      
      
      <!-- <div class="col-auto d-flex justify-content-center">
        <div class="dropdown">
          <button
            class="btn"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            @click.stop
          >
            <FontAwesomeIcon icon="fa-solid fa-ellipsis-vertical" />
          </button>
          <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="actionsDropdown">
            <li>
              <a class="dropdown-item" href="#" @click="downloadClick">
                <FontAwesomeIcon icon="fa-solid fa-download" class="me-2" /> Download
              </a>
            </li>
            <li>
              <a class="dropdown-item text-danger" href="#" @click="deleteClick">
                <FontAwesomeIcon icon="fa-solid fa-trash" class="me-2" /> Delete
              </a>
            </li>
          </ul>
        </div>
      </div> -->

      <div style="justify-content: space-between;" class="row d-flex mt-4">
        <div class="col-auto">
          <button
            class="btn font-weight-light text-capitalize my-0"
            aria-label="Download file"
            :style="{ 
              border: '0.5px solid rgb(229, 231, 235)', 
              color: isDownloading ? '#999' : 'black', 
              textDecoration: 'none',
              opacity: isDownloading ? '0.6' : '1',
              cursor: isDownloading ? 'not-allowed' : 'pointer'
            }"
            :disabled="isDownloading"
            @click="downloadClick"
          >
            <FontAwesomeIcon 
              v-if="!isDownloading" 
              icon="fa-solid fa-download" 
              style="font-size: 14px;" 
            />
            <div 
              v-else 
              class="spinner-border spinner-border-sm" 
              style="font-size: 14px; width: 14px; height: 14px;"
              role="status"
            >
              <span class="visually-hidden">Downloading...</span>
            </div>
            &nbsp; {{ isDownloading ? 'Downloading...' : 'Download' }}
          </button>
        </div>
        <div class="col-auto my-2 px-0"> 
          <span class="file-meta text-sm mx-2 my-2">Shared by: &nbsp;</span> 
          <a
            class="avatar avatar-xs rounded-circle"
            data-bs-toggle="tooltip"
            data-bs-placement="bottom"
            :title="ownerName"
          >
           <div class="pa-4">
              <material-avatar
                img=""
                :alt="ownerName"
                :color="ownerColor"
                size="sm"
                shadow="sm"
                circular
              />
            </div>
          </a>
        </div>
      </div>
      <div class="col-12 text-center arrow-container mt-3">
        <material-progress v-if="showProgress" color="success" :percentage="progressPercentage" />
        <div v-if="downloadStatus" class="text-xs text-muted mt-1">
          {{ downloadStatus }}
        </div>
      </div>
    </div>

  </div>

</template>

<script>
import { mapActions, mapState } from "vuex";
import MaterialProgress from "@/components/ui/MaterialProgress.vue";
import MaterialAvatar from '@/components/ui/MaterialAvatar';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { determineFileIcon, determineFileColor, determineFileType } from "@/utils/files.js";
export default {
  name: 'SharedFileCard',
  components: {
    MaterialProgress,
    MaterialAvatar,
    FontAwesomeIcon
  },
  props: {
    fileId: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    fileType: {
      type: String,
      default: "Unknown"
    },
    dateTime: {
      type: String,
      default: ""
    },
    owner: {
      type: String,
      default: ""
    },
    contentType: {
      type: String,
      default: ""
    },
    size: {
      type: String,
      default: ""
    },
    isEncrypted: {
      type: Boolean,
      default: false
    }
  },
  emits: ["file-deleted"],
  data() {
    return {
      showProgress: false,
      progressPercentage: 0,
      downloadStatus: '',
      isDownloading: false
    };
  },
  computed: {
    ...mapState("contacts", ["contactList"]),
    ownerName() {
      const contactFound = this.contactList.filter(contact => contact.email === this.owner);
      return contactFound && contactFound[0] ? contactFound[0].name : this.owner;
    },
    ownerColor() {
      const contactFound = this.contactList.filter(contact => contact.email === this.owner);
      return contactFound && contactFound[0] ? contactFound[0].color : null;
    },
    icon() {
      return determineFileIcon(determineFileType(this.title || ''))
    },
    color() {
      return determineFileColor(determineFileType(this.title || ''))
    }
  },
  methods: {
    ...mapActions("files", ["deleteFileAction", "downloadSharedFileAction"]),
    ...mapActions("snackbar", ["showSnackbar"]),
    downloadClick() {
      if (this.isDownloading) {
        return;
      }
      
      this.isDownloading = true;
      this.showProgress = true;
      this.progressPercentage = 0;
      this.downloadStatus = 'Starting download...';
      
      this.downloadSharedFileAction({
        fileId: this.fileId,
        onProgress: (progress, progressInfo) => {
          this.progressPercentage = Number(progress.toFixed(2));
          this.downloadStatus = progressInfo?.status || 'Downloading...';
        }
      }).then(() => {
        this.showProgress = false;
        this.progressPercentage = 0;
        this.downloadStatus = '';
        this.isDownloading = false;
      }).catch(() => {
        this.showProgress = false;
        this.progressPercentage = 0;
        this.downloadStatus = '';
        this.isDownloading = false;
        this.showSnackbar({ title: "Download failed", color: "danger" });
      });
    },
    deleteClick() {
      this.deleteFileAction(this.fileId);
      this.$emit("file-deleted", this.fileId);
    }
  }
}
</script>

<style scoped>
.file-card {
  background-color: rgba(255, 255, 255, 0.85);
  border-radius: 8px;
  padding: 16px;
  color: #333;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.file-meta {
  font-size: 12px;
  color: #666;
}
</style>